import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { CartContext } from "../context/CartContext";
import axios from "axios";
import "../styles/NavigationMenu.css";

const NavigationMenu = () => {
    const { isLoggedIn, logout } = useContext(AuthContext);
    
    // ZAKTUALIZOWANE: Pobranie funkcji do edycji i usuwania produktów z CartContext
    const { 
        cart, 
        fetchCart, 
        loading, 
        removeFromCart, 
        updateItemQuantity 
    } = useContext(CartContext);

    const totalCartSum = cart.items.reduce(
		(sum, item) => sum + item.product.price * item.quantity,
		0
	);


    const [menuOpen, setMenuOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [cartOpen, setCartOpen] = useState(false);
    const [userDetails, setUserDetails] = useState({});
    const [categories, setCategories] = useState([]);
    const [categoriesOpen, setCategoriesOpen] = useState(false);
    const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
    const [openCategoryId, setOpenCategoryId] = useState(null);

    const navigate = useNavigate();

    const profileRef = useRef(null);
    const cartRef = useRef(null);
    const categoriesRef = useRef(null);

    /* -------------------- Close dropdowns on outside click -------------------- */
    useEffect(() => {
        const handleCloseDropdowns = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setProfileOpen(false);
            }
            if (cartRef.current && !cartRef.current.contains(e.target)) {
                setCartOpen(false);
            }
            if (categoriesRef.current && !categoriesRef.current.contains(e.target)) {
                setCategoriesOpen(false);
            }
        };

        document.addEventListener("mousedown", handleCloseDropdowns);
        return () => document.removeEventListener("mousedown", handleCloseDropdowns);
    }, []);

    /* -------------------- Fetch user details -------------------- */
    useEffect(() => {
        if (!isLoggedIn) return;

        const fetchUserDetails = async () => {
            try {
                const token = localStorage.getItem("authToken");
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/users/user`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setUserDetails(response.data);
            } catch (err) {
                logout();
                navigate("/login");
            }
        };

        fetchUserDetails();
    }, [isLoggedIn, logout, navigate]);

    /* -------------------- Fetch categories -------------------- */
    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_API_URL}/api/categories`)
            .then((res) => {
                const data = res.data || [];
                
                const sortedCategories = data.sort((a, b) => {
                    const nameA = a.name ? a.name.trim() : "";
                    const nameB = b.name ? b.name.trim() : "";

                    const startsWithKompozycjeA = nameA.toLowerCase().startsWith("kompozycje");
                    const startsWithKompozycjeB = nameB.toLowerCase().startsWith("kompozycje");

                    if (startsWithKompozycjeA && startsWithKompozycjeB) {
                        return nameA.localeCompare(nameB, "pl", { sensitivity: "base" });
                    }
                    if (startsWithKompozycjeA) return -1;
                    if (startsWithKompozycjeB) return 1;

                    return nameA.localeCompare(nameB, "pl", { sensitivity: "base" });
                });

                setCategories(sortedCategories);
            })
            .catch((err) =>
                console.error("Błąd pobierania kategorii:", err)
            );
    }, []);

    /* -------------------- Fetch cart when opened -------------------- */
    useEffect(() => {
        if (cartOpen && isLoggedIn) {
            fetchCart();
        }
    }, [cartOpen, isLoggedIn, fetchCart]);

    

    /* -------------------- Handlers -------------------- */
    const handleLogout = () => {
        setMenuOpen(false);
        logout();
        navigate("/login");
    };

    const handleGoToCart = () => {
        navigate("/profile", { state: { tab: "cart" } });
        setMenuOpen(false);
        setCartOpen(false);
    };

    const toggleCategory = (id) => {
        setOpenCategoryId(prev => (prev === id ? null : id));
    };

    const cartCount = cart?.items?.length || 0;

    /* -------------------- JSX -------------------- */
    return (
        <nav className="nav">
            <div className="nav__container">
                <Link to="/" className="nav__logo">
                    <img src="/assets/logo.jpg" alt="Logo" />
                </Link>

                <button
                    className="nav__hamburger"
                    onClick={() => setMenuOpen((prev) => !prev)}
                >
                    <span />
                    <span />
                    <span />
                </button>

                <ul className="nav__menu">
                    <li><Link to="/">Strona główna</Link></li>

                    {/* Produkty */}
                    <li className="nav__item nav__categories" ref={categoriesRef}>
                        <button
                            className="nav__categories-btn"
                            onClick={() => setCategoriesOpen((prev) => !prev)}
                        >
                            Produkty ▾
                        </button>

                        {categoriesOpen && (
                            <div className="categories-dropdown">
                                <Link
                                    to="/products"
                                    className="category-link"
                                    onClick={() => setCategoriesOpen(false)}
                                >
                                    Wszystkie
                                </Link>

                                {categories.map((cat) => {
                                    const isOpen = openCategoryId === cat._id;

                                    return (
                                        <div key={cat._id} className="category-item">
                                            <button
                                                className="category-header"
                                                onClick={() => toggleCategory(cat._id)}
                                            >
                                                <span>{cat.name}</span>
                                                <span className="arrow-nav-menu-desktop">
                                                    {isOpen ? "▲" : "▾"}
                                                </span>
                                            </button>

                                            {isOpen && (
                                                <div className="subcategories">
                                                    <Link
                                                        to={`/products?category=${cat._id}`}
                                                        className="subcategory-link all"
                                                        onClick={() => setCategoriesOpen(false)}
                                                    >
                                                        Wszystko z kategorii {cat.name}
                                                    </Link>

                                                    {cat.subcategories?.map((s) => (
                                                        <Link
                                                            key={s._id || s.name}
                                                            to={`/products?category=${cat._id}&subCategory=${encodeURIComponent(s.name)}`}
                                                            className="subcategory-link"
                                                            onClick={() => setCategoriesOpen(false)}
                                                        >
                                                            {s.name}
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </li>

                    <li><Link to="/about">O nas</Link></li>
                    <li><Link to="/contact">Kontakt</Link></li>

                    {/* Cart */}
                    {isLoggedIn && (
                        <li className="nav__item" ref={cartRef}>
                            <button
                                className="nav__icon-btn"
                                onClick={() => setCartOpen((prev) => !prev)}
                            >
                                🛒 <span className="nav__badge">{cartCount}</span>
                            </button>

                            {cartOpen && (
                                <div className="dropdown dropdown--cart">
                                    {loading ? (
                                        <p>Ładowanie...</p>
                                    ) : cart?.items?.length === 0 ? (
                                        <p>Koszyk jest pusty</p>
                                    ) : (
                                        <>
                                            <ul className="dropdown__list dropdown__list--scrollable">
                                                {cart.items.map((item) => (
                                                    <li key={item.product._id} className="dropdown__item dropdown__item--cart-row">
                                                        <img
                                                            className="dropdown__cart-img"
                                                            src={(() => {
                                                                const img = item.product.images?.[0];
                                                                if (!img) return '/no-image.png';
                                                                if (typeof img === 'string' && (img.startsWith('http') || img.startsWith('data:'))) return img;
                                                                return `${process.env.REACT_APP_API_URL}${img}`;
                                                            })()}
                                                            alt={item.product.name}
                                                        />
                                                        <div className="dropdown__cart-details">
                                                            <span className="dropdown__cart-name">{item.product.name}</span>
                                                            <div className="dropdown__cart-controls">
                                                                <button 
                                                                    onClick={() => updateItemQuantity(item.product._id, item.quantity - 1)}
                                                                    disabled={item.quantity <= 1}
                                                                    className="cart-control-btn"
                                                                >
                                                                    -
                                                                </button>
                                                                <span className="cart-qty">{item.quantity} szt.</span>
                                                                <button 
                                                                    onClick={() => updateItemQuantity(item.product._id, item.quantity + 1)}
                                                                    className="cart-control-btn"
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <button 
                                                            className="dropdown__cart-remove" 
                                                            onClick={() => removeFromCart(item.product._id)}
                                                            title="Usuń produkt"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                            <p className="total_cart_value"><strong>Suma wartości koszyka:</strong> {totalCartSum.toFixed(2)} zł</p>
                                            <button
                                                className="dropdown__btn"
                                                onClick={handleGoToCart}
                                            >
                                                Przejdź do koszyka
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </li>
                    )}

                    {/* User */}
                    <li className="nav__item" ref={profileRef}>
                        {isLoggedIn ? (
                            <>
                                <button
                                    className="nav__icon-btn"
                                    onClick={() => setProfileOpen((prev) => !prev)}
                                >
                                    👤 {userDetails.firstName || userDetails.email}
                                </button>

                                {profileOpen && (
                                    <div className="dropdown">
                                        <button onClick={() => { setMenuOpen(false); navigate("/profile"); }}>
                                            Profil
                                        </button>
                                        <button
                                            onClick={() =>
                                                navigate("/profile", { state: { tab: "orders" } })
                                            }
                                        >
                                            Zamówienia
                                        </button>
                                        <button onClick={handleGoToCart}>Koszyk</button>
                                        <button
                                            className="dropdown__logout"
                                            onClick={() => { setMenuOpen(false); handleLogout(); }}
                                        >
                                            Wyloguj
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <Link to="/login" className="nav__login">
                                Zaloguj się
                            </Link>
                        )}
                    </li>
                </ul>
            </div>

            {/* Mobile menu */}
            <div className={`mobile ${menuOpen ? "mobile--open" : ""}`}>
                <button
                    className="mobile__close"
                    onClick={() => setMenuOpen(false)}
                >
                    ✖
                </button>

                <Link to="/" onClick={() => setMenuOpen(false)}>
                    Strona główna
                </Link>

                <button
                    className="mobile__cat-toggle"
                    onClick={() => setMobileCatsOpen((prev) => !prev)}
                >
                    {mobileCatsOpen ? "Produkty ▲" : "Produkty ▾"}
                </button>

                {mobileCatsOpen && (
                    <div className="mobile-cats">
                        <div className="mobile-cat">
                            <Link
                                to="/products"
                                onClick={() => {
                                    setMenuOpen(false);
                                    setMobileCatsOpen(false);
                                }}
                            >
                                Wszystkie
                            </Link>
                        </div>

                        {categories.map((cat) => (
                            <div key={cat._id} className="mobile-cat">
                                <Link
                                    to={`/products?category=${cat._id}`}
                                    onClick={() => {
                                        setMenuOpen(false);
                                        setMobileCatsOpen(false);
                                    }}
                                >
                                    {cat.name}
                                </Link>

                                {cat.subcategories?.length > 0 && (
                                    <div className="mobile-subcats">
                                        {cat.subcategories.map((s) => (
                                            <Link
                                                key={s._id || s.name}
                                                to={`/products?category=${cat._id}&subCategory=${encodeURIComponent(
                                                    s.name
                                                )}`}
                                                onClick={() => {
                                                    setMenuOpen(false);
                                                    setMobileCatsOpen(false);
                                                }}
                                            >
                                                {s.name}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <Link to="/about" onClick={() => setMenuOpen(false)}>
                    O nas
                </Link>
                <Link to="/contact" onClick={() => setMenuOpen(false)}>
                    Kontakt
                </Link>

                {isLoggedIn ? (
                    <>
                        <button onClick={handleGoToCart}>
                            Koszyk ({cartCount})
                        </button>
                        <button onClick={() => { setMenuOpen(false); navigate("/profile"); }}>
                            Profil
                        </button>
                        <button
                            className="dropdown__logout"
                            onClick={handleLogout}
                        >
                            Wyloguj
                        </button>
                    </>
                ) : (
                    <Link
                        to="/login"
                        onClick={() => setMenuOpen(false)}
                        className="mobile__login"
                    >
                        Zaloguj się
                    </Link>
                )}
            </div>
        </nav>
    );
};

export default NavigationMenu;